"use client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getUserLocation } from "@/utils/location";

export interface UserLocation {
  lat: number;
  lng: number;
}

interface LocationContextValue {
  userLocation: UserLocation | null;
  locationPermissionDenied: boolean;
  showLocationPrompt: boolean;
  isRequestingLocation: boolean;
  locationError: string | null;
  handleRequestLocation: () => Promise<void>;
  dismissLocationPrompt: () => void;
}

const LocationContext = createContext<LocationContextValue | undefined>(
  undefined,
);

export function LocationProvider({ children }: { children: ReactNode }) {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationPermissionDenied, setLocationPermissionDenied] =
    useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const requestedRef = useRef(false);

  // Resolve the user's location exactly once for the whole app so consumers
  // (properties, managed properties, weekly deals, ...) never re-request the
  // browser geolocation every time a page mounts.
  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;

    const checkLocationPermission = async () => {
      try {
        const position = await getUserLocation();
        setUserLocation({ lat: position.lat, lng: position.lng });
        setLocationPermissionDenied(false);
      } catch (error: any) {
        if (error.message === "Location permission denied") {
          setLocationPermissionDenied(true);
          setShowLocationPrompt(true);
        }
      }
    };
    checkLocationPermission();
  }, []);

  const handleRequestLocation = useCallback(async () => {
    setIsRequestingLocation(true);
    setLocationError(null);
    try {
      const position = await getUserLocation();
      setUserLocation({ lat: position.lat, lng: position.lng });
      setLocationPermissionDenied(false);
      setShowLocationPrompt(false);
    } catch (error: any) {
      setLocationPermissionDenied(true);
      // Browsers won't re-show the permission prompt once access is blocked,
      // so a repeated denial fails silently — surface it instead of leaving
      // the user staring at an unresponsive banner.
      setLocationError(
        error?.message === "Location permission denied"
          ? "Location is blocked for this site — allow it in your browser's site settings, then try again."
          : "Couldn't get your location. Please try again.",
      );
    } finally {
      setIsRequestingLocation(false);
    }
  }, []);

  const dismissLocationPrompt = useCallback(() => {
    // In-memory only (no sessionStorage) so a page refresh re-shows the
    // banner whenever permission still hasn't been granted.
    setShowLocationPrompt(false);
    setLocationError(null);
  }, []);

  const value: LocationContextValue = {
    userLocation,
    locationPermissionDenied,
    showLocationPrompt,
    isRequestingLocation,
    locationError,
    handleRequestLocation,
    dismissLocationPrompt,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation(): LocationContextValue {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error("useLocation must be used within LocationProvider");
  return ctx;
}
